import type { BadgeProps } from '@/components/ui/Badge'
import i18n, { intlLocale } from '@/i18n'
import type { FeeFrequency, InvoiceStatus, PaymentMethod } from './types'

type Tone = NonNullable<BadgeProps['tone']>

export const invoiceStatusTone: Record<InvoiceStatus, Tone> = {
  paid: 'success',
  partial: 'warning',
  overdue: 'danger',
  unpaid: 'info',
  cancelled: 'neutral',
}

export const invoiceStatusLabel: Record<InvoiceStatus, string> = {
  paid: 'Paid',
  partial: 'Partially paid',
  overdue: 'Overdue',
  unpaid: 'Unpaid',
  cancelled: 'Cancelled',
}

export const methodLabel: Record<PaymentMethod, string> = {
  cash: 'Cash',
  bank_transfer: 'Bank transfer',
  cheque: 'Cheque',
  card: 'Card',
  mobile_money: 'Mobile money',
}

export const frequencyLabel: Record<FeeFrequency, string> = {
  monthly: 'Monthly',
  termly: 'Per term',
  yearly: 'Yearly',
  one_time: 'One-time',
}

const tf = (key: string, options?: Record<string, unknown>) => i18n.t(key, { ns: 'finance', ...options })

function monthYear(y: number, m: number): string {
  return new Intl.DateTimeFormat(intlLocale(), { month: 'long', year: 'numeric' }).format(
    new Date(y, m - 1, 1),
  )
}

/** "2026-09" → "September 2026" / "septembre 2026", "annual" → "Annual" / "Annuel". */
export function periodLabel(period: string): string {
  if (period === 'annual') return tf('Annual')
  const [y, m] = period.split('-').map(Number)
  if (!y || !m) return period
  return monthYear(y, m)
}

const EN_MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

/**
 * Localises a period label produced by the API in English ("September 2026", "Annual 2026–2027").
 * Anything unrecognised is returned unchanged.
 */
export function localizePeriodLabel(label: string): string {
  const annual = /^Annual (.+)$/.exec(label)
  if (annual) return tf('Annual {{year}}', { year: annual[1] })
  const month = /^([A-Z][a-z]+) (\d{4})$/.exec(label)
  if (month) {
    const m = EN_MONTHS.indexOf(month[1] ?? '')
    if (m >= 0) return monthYear(Number(month[2]), m + 1)
  }
  return label
}
